import { AI_MODELS_MODULE_ID, AI_MODELS_REGISTER_ID } from '../pages/portal/ai-models/aiModelConstants';
import {
  GOVERNED_MCP_GATEWAY_API_KEYS_ID,
  GOVERNED_MCP_GATEWAY_CONFIGURATION_ID,
  GOVERNED_MCP_GATEWAY_DASHBOARD_ID,
  GOVERNED_MCP_GATEWAY_MODULE_ID,
  GOVERNED_MCP_GATEWAY_PAGE_IDS,
  GOVERNED_MCP_GATEWAY_PATHS,
  GOVERNED_MCP_GATEWAY_TOOLS_ID,
  MCP_ACCESS_MANAGER_MODULE_ID,
  MCP_RESOURCES_MODULE_ID,
} from '../pages/portal/governed-mcp-gateway/governedMcpConstants';
import { LLM_GATEWAY_PAGE_ID, LLM_GATEWAY_PATHS } from '../pages/portal/llm-gateway/llmGatewayConstants';
import {
  isOmniChatChildId,
  isOmniChatPageId,
  OMNICHAT_DETAIL_ID,
  OMNICHAT_LISTING_ID,
  OMNICHAT_PAGE_ID,
  OMNICHAT_PATHS,
} from '../pages/portal/omnichat/omniChatConstants';
import { OAUTH_APPS_PAGE_ID, OAUTH_APPS_PATH } from '../pages/portal/oauth-apps/oauthAppsConstants';
import {
  DATA_INGESTION_PAGE_ID,
  DATA_INGESTION_PATH,
  DATA_MODELS_PAGE_ID,
  DATA_MODELS_PATH,
  ENTITIES_PAGE_ID,
  isLegacyInheritingDataPageId,
} from '../pages/portal/data-models/dataModelsConstants';

export interface RoutePrivilegeNode {
  id: string;
  hasAccess?: boolean;
  path?: string | null;
  children?: RoutePrivilegeNode[];
}

/** Always-allowed escape hatch so users are never fully locked out. */
export const ALWAYS_ALLOWED_PATHS = ['/portal/my-account', '/portal/no-access'] as const;

export function hasGrantedRoutes(routes: RoutePrivilegeNode[]): boolean {
  const walk = (nodes: RoutePrivilegeNode[]): boolean =>
    nodes.some((route) => route.hasAccess === true || (route.children?.length ? walk(route.children) : false));
  return walk(routes);
}

/**
 * Real router path prefixes → privilege catalog ids (Sidebar / mockRoutes).
 * Longest-prefix match wins — keep more specific paths before checking shorter ones via sort.
 */
export const PORTAL_ROUTE_PRIVILEGES: ReadonlyArray<{ pathPrefix: string; id: string }> = [
  { pathPrefix: '/portal/home', id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890' },
  { pathPrefix: '/portal/workflows', id: 'b2c3d4e5-f6g7-8901-bcde-f12345678901' },
  { pathPrefix: '/portal/orchestration', id: 'b2c3d4e5-f6g7-8901-bcde-f12345678901' },
  { pathPrefix: '/portal/flowbuilder', id: 'b2c3d4e5-f6g7-8901-bcde-f12345678901' },
  { pathPrefix: '/portal/applications', id: '6f3a0f4c-20b7-4c2a-b4c1-7caaed2358b7' },
  { pathPrefix: '/portal/catalog', id: 'e8c4a1b2-7d3f-4a91-8e5c-2b6f9d0a4c17' },
  { pathPrefix: '/portal/ai-models/register', id: AI_MODELS_REGISTER_ID },
  { pathPrefix: '/portal/ai-models', id: AI_MODELS_MODULE_ID },
  { pathPrefix: '/portal/chatagent', id: '4e72e1aa-8c6d-4d2b-bd35-0df9a5df4d01' },
  { pathPrefix: '/portal/createchatagent', id: '4e72e1aa-8c6d-4d2b-bd35-0df9a5df4d01' },
  { pathPrefix: '/portal/chatbot', id: '4e72e1aa-8c6d-4d2b-bd35-0df9a5df4d01' },
  { pathPrefix: '/portal/agents', id: 'd918c2ef-0c74-46c7-b50d-dcf4d0d7c59d' },
  { pathPrefix: '/portal/agentStudio', id: 'd918c2ef-0c74-46c7-b50d-dcf4d0d7c59d' },
  { pathPrefix: OMNICHAT_PATHS.create, id: OMNICHAT_LISTING_ID },
  { pathPrefix: OMNICHAT_PATHS.list, id: OMNICHAT_LISTING_ID },
  { pathPrefix: '/portal/knowledge-base', id: 'h8i9j0k1-l2m3-4567-2345-678901234567' },
  { pathPrefix: '/portal/analytics-hub', id: '70b4dff6-7d13-43b0-a6d6-cba6b5b4a8ce' },
  { pathPrefix: DATA_MODELS_PATH, id: DATA_MODELS_PAGE_ID },
  { pathPrefix: DATA_INGESTION_PATH, id: DATA_INGESTION_PAGE_ID },
  { pathPrefix: '/portal/configurations/llm-routing', id: 'k1l2m3n4-o5p6-7890-5678-901234567890' },
  { pathPrefix: '/portal/configurations/data-vault', id: 'o5p6q7r8-s9t0-1234-9012-345678901234' },
  { pathPrefix: '/portal/configurations/schedules', id: 's9t0u1v2-w3x4-5678-3456-789012345678' },
  { pathPrefix: '/portal/mcpresources', id: MCP_RESOURCES_MODULE_ID },
  { pathPrefix: '/portal/mcplivemonitor', id: 'd4a5e6f7-8b9c-40d1-2e34-5f6a7b8c9d0e' },
  { pathPrefix: GOVERNED_MCP_GATEWAY_PATHS.dashboard, id: GOVERNED_MCP_GATEWAY_DASHBOARD_ID },
  { pathPrefix: GOVERNED_MCP_GATEWAY_PATHS.tools, id: GOVERNED_MCP_GATEWAY_TOOLS_ID },
  { pathPrefix: GOVERNED_MCP_GATEWAY_PATHS.configuration, id: GOVERNED_MCP_GATEWAY_CONFIGURATION_ID },
  { pathPrefix: GOVERNED_MCP_GATEWAY_PATHS.aiUsage, id: GOVERNED_MCP_GATEWAY_API_KEYS_ID },
  { pathPrefix: GOVERNED_MCP_GATEWAY_PATHS.root, id: GOVERNED_MCP_GATEWAY_MODULE_ID },
  { pathPrefix: LLM_GATEWAY_PATHS.list, id: LLM_GATEWAY_PAGE_ID },
  { pathPrefix: '/portal/ai-governance', id: 'g1h2i3j4-k5l6-7890-abcd-ef12345678901' },
  { pathPrefix: '/portal/connections', id: 'e1f2g3h4-i5j6-7890-5678-901234567890' },
  { pathPrefix: OAUTH_APPS_PATH, id: OAUTH_APPS_PAGE_ID },
  { pathPrefix: '/portal/access-settings', id: 'h4i5j6k7-l8m9-0123-8901-234567890123' },
  { pathPrefix: '/portal/system-settings', id: 'm9n0o1p2-q3r4-5678-3456-789012345678' },
  { pathPrefix: '/portal/my-account', id: 'n0o1p2q3-r4s5-6789-4567-890123456789' },
  { pathPrefix: '/portal/logs', id: 'o1p2q3r4-s5t6-7890-5678-901234567890' },
  { pathPrefix: '/portal/deployments', id: 'a7b8c9d0-e1f2-3456-1234-567890123456' },
  { pathPrefix: '/portal/intent-orchestration', id: 'w3x4y5z6-a7b8-9012-7890-123456789012' },
  { pathPrefix: '/portal/reports', id: 'p2q3r4s5-t6u7-8901-6789-012345678901' },
];

const SORTED_PRIVILEGES = [...PORTAL_ROUTE_PRIVILEGES].sort(
  (a, b) => b.pathPrefix.length - a.pathPrefix.length
);

export function normalizePortalPath(path: string): string {
  if (!path) return path;
  if (path.startsWith('/portal') || path.startsWith('/chat')) return path;
  return `/portal/${path.replace(/^\/+/, '')}`;
}

export function normalizeFirstPagePath(path: string): string | null {
  const trimmed = path.trim();
  if (!trimmed) return null;
  if (trimmed === '/' || trimmed === '/portal' || trimmed === '/portal/') return '/portal/home';
  return normalizePortalPath(trimmed);
}

function pathMatchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function resolveOmniChatPrivilegeId(pathname: string): string | null {
  if (pathname === OMNICHAT_PATHS.list || pathname === OMNICHAT_PATHS.create) {
    return OMNICHAT_LISTING_ID;
  }
  if (pathname.startsWith(`${OMNICHAT_PATHS.list}/`)) {
    return OMNICHAT_DETAIL_ID;
  }
  return null;
}

export function resolvePrivilegeIdForPath(pathname: string): string | null {
  const normalized = pathname.startsWith('/portal') || pathname.startsWith('/chat')
    ? pathname
    : normalizePortalPath(pathname);

  const omniChatId = resolveOmniChatPrivilegeId(normalized);
  if (omniChatId) return omniChatId;

  for (const entry of SORTED_PRIVILEGES) {
    if (pathMatchesPrefix(normalized, entry.pathPrefix)) {
      return entry.id;
    }
  }
  return null;
}

function findAccessFlag(
  id: string,
  routes: RoutePrivilegeNode[]
): boolean | undefined {
  for (const route of routes) {
    if (route.id === id) return route.hasAccess ?? false;
    if (route.children?.length) {
      const nested = findAccessFlag(id, route.children);
      if (nested !== undefined) return nested;
    }
  }
  return undefined;
}

function isGovernedMcpGatewayPageId(id: string): boolean {
  return (GOVERNED_MCP_GATEWAY_PAGE_IDS as readonly string[]).includes(id);
}

/**
 * Missing privilege id ⇒ denied, except FO-only modules not yet in stored IAM
 * catalogs (existing sessions would otherwise be locked out of the new route).
 * Gateway pages inherit an explicit parent/group grant when the child id is absent.
 */
export function checkPrivilegeAccess(
  id: string,
  routes: RoutePrivilegeNode[]
): boolean {
  const flag = findAccessFlag(id, routes);
  if (flag !== undefined) return flag;

  if (isLegacyInheritingDataPageId(id)) {
    const legacy = findAccessFlag(ENTITIES_PAGE_ID, routes);
    if (legacy !== undefined) return legacy;
  }

  if (isOmniChatChildId(id)) {
    const parent = findAccessFlag(OMNICHAT_PAGE_ID, routes);
    if (parent !== undefined) return parent;
  }

  if (id === LLM_GATEWAY_PAGE_ID || isGovernedMcpGatewayPageId(id)) {
    const parent = findAccessFlag(GOVERNED_MCP_GATEWAY_MODULE_ID, routes);
    if (parent !== undefined) return parent;
    const group = findAccessFlag(MCP_ACCESS_MANAGER_MODULE_ID, routes);
    if (group !== undefined) return group;
  }

  if (!hasGrantedRoutes(routes)) return false;
  return (
    id === AI_MODELS_MODULE_ID ||
    id === AI_MODELS_REGISTER_ID ||
    id === GOVERNED_MCP_GATEWAY_MODULE_ID ||
    id === LLM_GATEWAY_PAGE_ID ||
    id === OAUTH_APPS_PAGE_ID ||
    id === DATA_MODELS_PAGE_ID ||
    id === DATA_INGESTION_PAGE_ID ||
    isOmniChatPageId(id) ||
    isGovernedMcpGatewayPageId(id)
  );
}

/**
 * Returns whether the current pathname is allowed given privilege routes.
 * - Always-allowed paths (my-account) → true
 * - Mapped path with hasAccess → that flag
 * - Mapped path missing from routes / hasAccess false → false
 * - Unmapped path → true (auth-only; not in privilege catalog)
 */
export function canAccessPath(
  pathname: string,
  routes: RoutePrivilegeNode[]
): boolean {
  if (ALWAYS_ALLOWED_PATHS.some((p) => pathMatchesPrefix(pathname, p))) {
    return true;
  }

  const privilegeId = resolvePrivilegeIdForPath(pathname);
  if (!privilegeId) return true;

  return checkPrivilegeAccess(privilegeId, routes);
}

export function getUserRoutesFromStorage(): RoutePrivilegeNode[] {
  try {
    const parsed = JSON.parse(localStorage.getItem('user_routes') || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
